import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './IntroVideoPreview.module.sass';
import {
  getIntroVideoFullUrl,
  getIntroVideoHlsUrl,
  getIntroVideoHoverPreviewUrl,
  getIntroVideoPosterUrl,
} from '../../utils/cloudinaryVideo';

const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const IntroVideoPreview = ({
  className,
  mode = 'card',
  posterUrl,
  previewUrl,
  fullUrl,
  hlsUrl,
  publicId,
  fallbackImageUrl,
  alt = 'Intro video',
}) => {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sources = useMemo(() => {
    const image = fallbackImageUrl || posterUrl || getIntroVideoPosterUrl(publicId);
    return {
      image,
      poster: posterUrl || image,
      preview: previewUrl || getIntroVideoHoverPreviewUrl(publicId) || fullUrl,
      full: fullUrl || getIntroVideoFullUrl(publicId),
      hls: hlsUrl || getIntroVideoHlsUrl(publicId),
    };
  }, [fallbackImageUrl, fullUrl, hlsUrl, posterUrl, previewUrl, publicId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHovering) return undefined;

    video.muted = true;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});

    return () => {
      video.pause();
      video.currentTime = 0;
    };
  }, [isHovering]);

  if (!sources.image && !sources.full && !sources.preview) return null;

  const handlePointerEnter = () => {
    if (canHover() && sources.preview) setIsHovering(true);
  };

  const handlePointerLeave = () => {
    setIsHovering(false);
    setIsMuted(true);
  };

  const handleExpand = (event) => {
    if (!sources.full) return;
    event.preventDefault();
    event.stopPropagation();
    setIsModalOpen(true);
  };

  const handleMuteToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const nextMuted = !isMuted;
    const video = videoRef.current;
    if (video) {
      video.muted = nextMuted;
      if (!nextMuted) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {
            video.muted = true;
            setIsMuted(true);
          });
        }
      }
    }
    setIsMuted(nextMuted);
  };

  return (
    <>
      <div
        className={cn(styles.preview, styles[mode], className)}
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
      >
        {sources.image && <img src={sources.image} alt={alt} className={cn(styles.poster, 'loaded')} />}
        {isHovering && sources.preview && (
          <video
            ref={videoRef}
            className={styles.video}
            src={sources.preview}
            muted={isMuted}
            loop
            playsInline
            preload="none"
          />
        )}
        {sources.preview && (
          <div className={cn(styles.controls, { [styles.visible]: isHovering || mode === 'hero' })}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleMuteToggle}
              aria-label={isMuted ? 'Unmute intro video' : 'Mute intro video'}
            >
              {isMuted ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  <path d="m16 9 5 5" />
                  <path d="m21 9-5 5" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  <path d="M15 9.5a4 4 0 0 1 0 5" />
                  <path d="M17.5 7a8 8 0 0 1 0 10" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleExpand}
              aria-label="Expand intro video"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 3H3v5" />
                <path d="m3 3 7 7" />
                <path d="M16 3h5v5" />
                <path d="m21 3-7 7" />
                <path d="M8 21H3v-5" />
                <path d="m3 21 7-7" />
                <path d="M16 21h5v-5" />
                <path d="m21 21-7-7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {isModalOpen &&
        createPortal(
          <div className={styles.overlay} onClick={() => setIsModalOpen(false)}>
            <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
              <button type="button" className={styles.closeButton} onClick={() => setIsModalOpen(false)}>
                Close
              </button>
              <video
                className={styles.player}
                src={sources.full}
                poster={sources.poster}
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default IntroVideoPreview;
